// src/pages/Districts.jsx
import React from 'react';
import styled from 'styled-components';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PublicAPI } from '../api/public';
import { normalizeMediaUrl } from '../api/base';

const Wrap = styled.main`width:min(1400px,96vw); margin:20px auto; padding:0 18px;`;
const Head = styled.section`display:grid; gap:6px; margin-bottom:8px;`;
const Filters = styled.div`
  display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-top:6px;
  input, select { border:1px solid var(--bdr); background:var(--card); border-radius:10px; padding:8px 10px; }
  .btn { padding:8px 12px; border:1px solid var(--bdr); border-radius:10px; background:var(--card); box-shadow:var(--shadow); }
  .btn-ghost { padding:8px 12px; border:1px solid var(--bdr); border-radius:10px; background:transparent; }
`;
const Grid = styled.section`
  display:grid; gap:12px; grid-template-columns: repeat(4, minmax(0,1fr));
  @media (max-width: 1100px){ grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 820px){ grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 520px){ grid-template-columns: 1fr; }
`;
const Card = styled(Link)`
  border:1px solid var(--bdr); border-radius:16px; background:var(--card); box-shadow:var(--shadow);
  overflow:hidden; color:inherit; text-decoration:none; display:flex; flex-direction:column;
  .img{ height:170px; background:#0d0f12; overflow:hidden; }
  .img img{ width:100%; height:100%; object-fit:cover; display:block; }
  .body{ padding:12px; display:grid; gap:6px; }
  .title{ font-weight:800; }
  .ex{ color:var(--muted); font-size:13px; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
`;

const firstImage = (d) => {
  const list = Array.isArray(d?.images) ? d.images
            : Array.isArray(d?.photos)  ? d.photos
            : Array.isArray(d?.gallery) ? d.gallery
            : [];
  
  if (!list.length) return '';
  
  const first = list[0];
  
  // Если это объект медиа, извлекаем URL
  if (typeof first === 'object' && first !== null) {
    const url = first.url || first.thumbnail_url || first.display_url || first.src;
    return url ? normalizeMediaUrl(url) : '';
  }
  
  // Если это строка, используем как есть
  if (typeof first === 'string') {
    return normalizeMediaUrl(first);
  }
  
  return '';
};

export default function Districts(){
  const { t, i18n } = useTranslation();
  const [sp, setSp] = useSearchParams();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [items, setItems] = React.useState([]);

  const [regionId, setRegionId] = React.useState(sp.get('region_id') || '');

  React.useEffect(()=>{
    let cancelled = false;
    (async ()=>{
      setLoading(true); setError('');
      try{
        const res = await PublicAPI.districts({
          region_id: regionId || undefined,
          locale: i18n.language || 'ru',
        });
        // API возвращает {districts: [...]}
        const list = Array.isArray(res?.districts) ? res.districts 
                   : Array.isArray(res?.items) ? res.items 
                   : Array.isArray(res) ? res 
                   : [];
        if(!cancelled) setItems(list);
      }catch(e){ if(!cancelled) { setItems([]); setError(e?.message || 'Error'); } }
      finally{ if(!cancelled) setLoading(false); }
    })();
    return ()=>{ cancelled = true; };
  }, [regionId, i18n.language]);

  return (
    <Wrap>
      <Head>
        <h1>{t('nav.districts','Районы')}</h1>
        <Filters>
          <input
            type="number"
            inputMode="numeric"
            placeholder={i18n.language==='en'?'Region ID (optional)':'ID региона (необязательно)'}
            value={regionId}
            onChange={e=>{
              const v = e.target.value.replace(/[^\d]/g,'');
              setRegionId(v);
            }}
            style={{width:200}}
          />
          <button
            className="btn"
            onClick={()=> setSp(s=>{
              const next = new URLSearchParams(s);
              if (regionId) next.set('region_id', regionId); else next.delete('region_id');
              return next;
            })}
          >
            {i18n.language==='en' ? 'Apply' : 'Применить'}
          </button>
          <button
            className="btn-ghost"
            onClick={()=>{ setRegionId(''); setSp(new URLSearchParams(), { replace:true }); }}
          >
            {i18n.language==='en' ? 'Reset' : 'Сбросить'}
          </button>
        </Filters>
      </Head>

      {loading && <div style={{opacity:.7}}>{t('common.loading','Загрузка…')}</div>}
      {error && <div style={{color:'#c62828'}}>{error}</div>}

      {!loading && !items.length && !error && (
        <div style={{opacity:.75}}>
          {i18n.language==='en' ? 'No districts found' : 'Районы не найдены'}
        </div>
      )}

      {!!items.length && (
        <Grid>
          {items.map((d,i)=>{
            const img = firstImage(d);
            const name = d?.name || d?.title || (i18n.language==='en' ? 'District' : 'Район');
            const excerpt = d?.excerpt || d?.subtitle || '';
            return (
              <Card key={d.slug || d.id || i} to={`/districts/${encodeURIComponent(d.slug || d.id)}`}>
                <div className="img">
                  <img
                    src={img || 'data:image/svg+xml;utf8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 400"><rect width="600" height="400" fill="#0d0f12"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#9aa0a6" font-family="sans-serif" font-size="18">No image</text></svg>')}
                    alt={name}
                    loading="lazy" decoding="async"
                  />
                </div>
                <div className="body">
                  <div className="title">{name}</div>
                  {!!excerpt && <div className="ex">{excerpt}</div>}
                </div>
              </Card>
            );
          })}
        </Grid>
      )}
    </Wrap>
  );
}
